
import React from 'react';
import { User, ArenaConfig } from '../../types';
import { 
  Trophy, Settings, ListOrdered, Grid, LogOut, Calendar, History, 
  ShieldCheck, Lock, AlertTriangle
} from 'lucide-react';

// --- Color sanitizer: prevents CSS injection via theme values ---
const sanitizeColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback;
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color.trim())) return color.trim();
  return fallback;
};

interface LayoutProps {
  config: ArenaConfig;
  currentUser: User | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  adminView: string;
  setAdminView: (view: any) => void;
  adminNotificationCount: number;
  onLogout: () => void;
  onShowSettings: () => void;
  children: React.ReactNode;
}

/**
 * SuspendedLayout: Pure "Under Construction" / Maintenance Page.
 * Strictly black, minimal, and premium.
 */
export const SuspendedLayout: React.FC<LayoutProps> = ({
  config,
  currentUser,
  isAdmin,
  isLoggedIn,
  adminView,
  setAdminView,
  adminNotificationCount,
  onLogout,
  onShowSettings,
  children
}) => {
  const primaryColor = sanitizeColor(config.theme?.primaryColor, '#f59e0b');

  const styleVars = `
    :root {
      --color-primary: ${primaryColor};
    }
    
    body {
      background-color: #000000 !important;
      color: #ffffff !important;
      margin: 0;
      padding: 0;
      overflow: hidden;
      font-family: 'Inter', sans-serif !important;
    }

    .admin-nav-item.active {
      background-color: var(--color-primary) !important;
      color: #000000 !important;
      font-weight: 900;
    }

    @keyframes pulse-slow {
      0%, 100% { opacity: 0.2; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(1.05); }
    }

    .bg-glow {
      position: absolute;
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, ${primaryColor}22 0%, transparent 70%);
      filter: blur(80px);
      z-index: 0;
      animation: pulse-slow 8s infinite ease-in-out;
    }
  `;

  // --- GUEST VIEW (STRICT UNDER CONSTRUCTION) ---
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden select-none">
        <style>{styleVars}</style>
        
        {/* Deep Atmosphere Glows */}
        <div className="bg-glow top-[-20%] left-[-10%]"></div>
        <div className="bg-glow bottom-[-20%] right-[-10%] [animation-delay:4s]"></div>

        <div className="z-10 flex flex-col items-center text-center px-6">
          <div className="mb-12 animate-fade-in">
             {config.logoUrl ? (
               <img src={config.logoUrl} alt="Logo" className="h-24 md:h-32 mx-auto object-contain grayscale brightness-200" />
             ) : (
               <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 mx-auto">
                 <Trophy size={40} strokeWidth={1.5} />
               </div>
             )}
          </div>

          <div className="space-y-4 max-w-2xl animate-fade-in-up">
            <h1 className="text-5xl md:text-7xl font-black text-white italic tracking-tighter uppercase leading-none">
              {config.name}
            </h1>
            <div className="flex items-center justify-center gap-4 py-6">
              <div className="h-px w-12 bg-white/10"></div>
              <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.5em]">Under Construction</span>
              <div className="h-px w-12 bg-white/10"></div>
            </div>
            <p className="text-white/40 text-lg md:text-xl font-medium tracking-tight">
              Wir bereiten aktuell etwas Großes vor.<br />
              Schau bald wieder vorbei.
            </p>
          </div>
        </div>

        {/* 
          Hidden Admin Portal: 
          We render children here BUT keep them strictly hidden (opacity-0) 
          UNLESS they are modals (which typically use fixed positioning and high z-index).
          SR-only ensures no screen bleed while maintaining basic component lifecycle.
        */}
        <div className="sr-only opacity-0 pointer-events-none absolute inset-0 overflow-hidden">
           {children}
        </div>
      </div>
    );
  }

  // --- ADMIN VIEW (REMAINS ACCESSIBLE) ---
  return (
    <div className="min-h-screen flex flex-col font-sans bg-[#050505] text-white">
      <style>{styleVars}</style>
      
      {/* Heavy Warning Banner */}
      <div className="bg-amber-500 text-black py-1.5 px-4 flex items-center justify-center gap-4 relative z-[100]">
        <AlertTriangle size={14} className="shrink-0" />
        <span className="text-[10px] font-black uppercase tracking-[0.2em]">Live-Wartung aktiv &bull; Gäste sehen nur die "Coming Soon" Seite</span>
        <AlertTriangle size={14} className="shrink-0" />
      </div>

      <header className="bg-black border-b border-white/5 px-4 md:px-8 py-4 flex flex-wrap md:flex-nowrap items-center justify-between sticky top-0 z-[80] shadow-2xl gap-y-2">
        <div className="flex items-center gap-3 order-1">
          {config.logoUrl ? (
            <img src={config.logoUrl} alt="Logo" className="w-10 h-10 object-contain grayscale brightness-200" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white bg-white/5 border border-white/10 shadow-lg">
                <Trophy size={20} />
            </div>
          )}
          <div>
            <h1 className="font-black text-sm md:text-base text-white tracking-tighter uppercase italic">{config.name}</h1>
            <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest leading-none block mt-0.5">Control Center</span>
          </div>
        </div>

        <div className="flex order-3 md:order-2 w-full md:w-auto justify-center mt-2 md:mt-0 p-1 rounded-2xl bg-white/5 border border-white/5">
          <button onClick={() => setAdminView('EDITOR')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${adminView === 'EDITOR' ? 'active' : 'text-white/40 hover:text-white'}`}>
            <Grid size={14} /> <span className="hidden lg:inline">Überblick</span>
          </button>
          <button onClick={() => setAdminView('RESERVATIONS')} className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${adminView === 'RESERVATIONS' ? 'active' : 'text-white/40 hover:text-white'}`}>
            <ListOrdered size={14} /> <span className="hidden lg:inline">Buchungen</span>
            {adminNotificationCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-black flex items-center justify-center text-[8px] text-white font-bold">{adminNotificationCount}</span>}
          </button>
          <button onClick={() => setAdminView('CALENDAR')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${adminView === 'CALENDAR' ? 'active' : 'text-white/40 hover:text-white'}`}>
            <Calendar size={14} /> <span className="hidden lg:inline">Kalender</span>
          </button>
          <button onClick={() => setAdminView('HISTORY')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-item ${adminView === 'HISTORY' ? 'active' : 'text-white/40 hover:text-white'}`}>
            <History size={14} /> <span className="hidden lg:inline">Historie</span>
          </button>
        </div>

        <div className="flex gap-2 md:gap-4 items-center order-2 md:order-3 ml-auto md:ml-0">
          <button onClick={onShowSettings} className="flex items-center justify-center w-10 h-10 rounded-xl text-white/40 hover:bg-white/10 transition-all border border-white/5" title="Einstellungen">
            <Settings size={20} /> 
          </button>
          <div className="flex items-center gap-4 pl-4 border-l border-white/10">
            <div className="text-right hidden sm:block">
                <div className="text-xs font-black text-white uppercase tracking-tight">{currentUser?.name}</div>
                <div className="text-[9px] font-bold text-amber-500/80 uppercase tracking-widest flex items-center justify-end gap-1">
                  <ShieldCheck size={10} /> Root Access
                </div>
            </div>
            <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all border border-red-500/10">
                <LogOut size={16} /> <span className="hidden md:inline">Exit</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 relative ${adminView === 'EDITOR' ? 'overflow-hidden' : 'overflow-auto'} p-4 md:p-10 bg-black`}>
        {children}
      </main>

      <footer className="py-4 px-8 border-t border-white/5 flex items-center justify-between opacity-30">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50">Maintenance Mode &bull; {config.tenantId}</span>
        <div className="flex items-center gap-2">
           <Lock size={12} className="text-white/50" />
           <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">Secure Access v3.1</span>
        </div>
      </footer>
    </div>
  );
};
